// ---------------------------------------------------------------------------
// Content Manager — data layer (MOCK)
// ---------------------------------------------------------------------------
// This module is the single seam between the Content Manager UI and its data
// source. Today it returns hard-coded mock data so the feature is fully usable
// without a backend. When the foodoai content API gains admin (cross-restaurant)
// endpoints — or you wire it per-restaurant with a provided token — replace the
// bodies of the three accessors below with real `fetch` calls. The UI consumes
// only these three functions, so nothing else has to change.
//
// Mock shapes deliberately mirror the foodoai OpenAPI schemas so the swap is a
// straight field-for-field map:
//
//   Restaurant  ← ProfileResponse  (GET /settings/profile)
//       id, restaurant_name, location, logo_url, subscription_plan
//       + content_count / last_generated_at (admin aggregate — not in today's spec)
//
//   ContentItem ← ContentResponse  (GET /api/v1/content)  /  ChatMediaItem
//       id (content_id), generation_record_id, type, content_type, content_stage,
//       status, content_url, thumbnail_url, caption, generation_prompt,
//       aspect_ratio, created_at
//
// Real wiring sketch (once a foodoai admin token is provided):
//   const FOODOAI_BASE = process.env.REACT_APP_FOODOAI_API_URL;   // e.g. https://api.foodoai.com
//   const headers = { Authorization: `Bearer ${foodoaiToken}` };
//   getRestaurants()            -> GET /admin/restaurants            (needs admin endpoint)
//   getRestaurantContent(id)    -> GET /admin/restaurants/{id}/content   OR
//                                  GET /api/v1/content (when scoped to one restaurant token)
//   deleteContentItem(genId)    -> DELETE /api/v1/chat/{generation_record_id}/history
// ---------------------------------------------------------------------------

// Deterministic placeholder media so the gallery looks real in the demo.
const img = (seed) => `https://picsum.photos/seed/${seed}/640/640`;

// Build a content item with foodoai-aligned fields.
const makeItem = (i, seed, type, content_type, content_stage, prompt, caption, created_at) => ({
  id: `content_${seed}_${i}`, // == ContentResponse.id / content_id
  content_id: `content_${seed}_${i}`,
  generation_record_id: `gen_${seed}_${i}`, // GenerationHistory.id — the delete key
  type, // "image" | "video"
  content_type, // "POST" | "REEL" | "STORY"
  content_stage, // "DRAFT" | "READY" | "SCHEDULED" | "PUBLISHED"
  status: "COMPLETED", // "COMPLETED" | "FAILED"
  content_url: img(`${seed}-${i}`),
  thumbnail_url: img(`${seed}-${i}`),
  aspect_ratio: type === "video" ? "9:16" : "1:1",
  caption,
  generation_prompt: prompt,
  ai_model: type === "video" ? "veo-3" : "imagen-4",
  is_favourite: i % 4 === 0,
  created_at,
  updated_at: created_at,
});

// Generate a believable history for one restaurant (count items).
const buildHistory = (seed, count) => {
  const types = ["image", "image", "image", "video"]; // mostly images
  const ctype = { image: "POST", video: "REEL" };
  const stages = ["PUBLISHED", "READY", "SCHEDULED", "DRAFT"];
  const prompts = [
    "Cinematic shot of a sizzling signature dish on a rustic table, warm lighting",
    "Top-down flat lay of the new seasonal menu, vibrant colours, marble surface",
    "Cozy interior ambience at golden hour, customers enjoying their meal",
    "Close-up of a freshly poured signature cocktail with garnish",
    "Chef plating a dessert, steam rising, moody background",
    "Reel: behind-the-scenes kitchen prep, fast cuts, upbeat vibe",
    "Weekend brunch spread with fresh juice and pastries, bright daylight",
    "Promotional poster for happy-hour offer, bold typography space",
  ];
  const captions = [
    "Taste the difference 🍽️ #foodie",
    "New on the menu this week ✨",
    "Your table is waiting 💚",
    "Happy hour, happier you 🍹",
    "Sweet endings 🍰",
    "Behind the scenes 👨‍🍳",
    "Brunch goals 🥐",
    "This weekend only — don't miss out!",
  ];
  const items = [];
  for (let i = 0; i < count; i++) {
    const type = types[i % types.length];
    // Spread created_at backwards from "now" by a few days each.
    const d = new Date(2026, 5, 18 - i, 14 - (i % 6), 30);
    items.push(
      makeItem(
        i,
        seed,
        type,
        ctype[type],
        stages[i % stages.length],
        prompts[i % prompts.length],
        captions[i % captions.length],
        d.toISOString()
      )
    );
  }
  return items;
};

// --- Mock restaurants (ProfileResponse-aligned) + their generated content. ----
const RESTAURANTS = [
  { id: "the-curry-house", restaurant_name: "The Curry House", location: "Manchester, UK", subscription_plan: "Pro", logo_url: img("logo-curry"), owner: "Rahul Sharma", count: 18 },
  { id: "bistro-21", restaurant_name: "Bistro 21", location: "London, UK", subscription_plan: "Enterprise", logo_url: img("logo-bistro"), owner: "Emma Watson", count: 32 },
  { id: "spice-garden", restaurant_name: "Spice Garden", location: "Birmingham, UK", subscription_plan: "Pro", logo_url: img("logo-spice"), owner: "Ali Hassan", count: 11 },
  { id: "ocean-bites", restaurant_name: "Ocean Bites", location: "Brighton, UK", subscription_plan: "Basic", logo_url: img("logo-ocean"), owner: "Sarah Lee", count: 4 },
  { id: "the-green-fork", restaurant_name: "The Green Fork", location: "Bristol, UK", subscription_plan: "Enterprise", logo_url: img("logo-green"), owner: "David Chen", count: 27 },
  { id: "golden-wok", restaurant_name: "Golden Wok", location: "Leeds, UK", subscription_plan: "Pro", logo_url: img("logo-wok"), owner: "Lin Zhang", count: 15 },
  { id: "pizza-piazza", restaurant_name: "Pizza Piazza", location: "Liverpool, UK", subscription_plan: "Basic", logo_url: img("logo-pizza"), owner: "Marco Rossi", count: 6 },
  { id: "sushi-spot", restaurant_name: "Sushi Spot", location: "Edinburgh, UK", subscription_plan: "Pro", logo_url: img("logo-sushi"), owner: "Yuki Tanaka", count: 21 },
  { id: "casa-blanca", restaurant_name: "Casa Blanca", location: "Glasgow, UK", subscription_plan: "Enterprise", logo_url: img("logo-casa"), owner: "Sofia Garcia", count: 39 },
];

// Pre-build each restaurant's content history once.
const HISTORY_BY_RESTAURANT = RESTAURANTS.reduce((acc, r) => {
  acc[r.id] = buildHistory(r.id, r.count);
  return acc;
}, {});

const derive = (r) => {
  const items = HISTORY_BY_RESTAURANT[r.id] || [];
  const images = items.filter((it) => it.type === "image").length;
  const videos = items.filter((it) => it.type === "video").length;
  const last = items.reduce(
    (max, it) => (it.created_at > max ? it.created_at : max),
    ""
  );
  return {
    id: r.id,
    restaurant_name: r.restaurant_name,
    location: r.location,
    subscription_plan: r.subscription_plan,
    logo_url: r.logo_url,
    owner: r.owner,
    content_count: items.length,
    image_count: images,
    video_count: videos,
    last_generated_at: last || null,
  };
};

// --- Public accessors (the only API the UI depends on) ----------------------

/** List all restaurants with aggregate content stats. */
export function getRestaurants() {
  return RESTAURANTS.map(derive);
}

/** Get one restaurant's profile + stats by id. */
export function getRestaurantById(id) {
  const r = RESTAURANTS.find((x) => x.id === id);
  return r ? derive(r) : null;
}

/** Get the full content/generation history for one restaurant (newest first). */
export function getRestaurantContent(id) {
  const items = HISTORY_BY_RESTAURANT[id] || [];
  return [...items].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

export const SUBSCRIPTION_PLANS = ["Basic", "Pro", "Enterprise"];
